// src/pages/AdminExtensionConfig.tsx
import React, { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'
import { Loader2, Save, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'

interface Cfg {
  minVersion: string
  latestVersion: string
  updateUrl?: string | null
  message?: string | null
  autoSyncEnabled: boolean
  autoSyncIntervalDays: number
  updatedAtUtc?: string | null
}

export default function AdminExtensionConfig() {
  const [cfg, setCfg] = useState<Cfg>({ minVersion: '', latestVersion: '', updateUrl: '', message: '', autoSyncEnabled: true, autoSyncIntervalDays: 7 })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await apiService.getAdminExtensionConfig()
      setCfg({
        minVersion: r.minVersion || '',
        latestVersion: r.latestVersion || '',
        updateUrl: r.updateUrl || '',
        message: r.message || '',
        autoSyncEnabled: r.autoSyncEnabled !== false,
        autoSyncIntervalDays: Number(r.autoSyncIntervalDays) || 7,
        updatedAtUtc: r.updatedAtUtc || null,
      })
    } catch { toast.error('Yüklenemedi') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const save = async () => {
    if (!cfg.minVersion.trim() || !cfg.latestVersion.trim()) {
      toast.error('Min Sürüm ve Son Sürüm zorunlu')
      return
    }
    setSaving(true)
    try {
      await apiService.updateAdminExtensionConfig({
        minVersion: cfg.minVersion.trim(),
        latestVersion: cfg.latestVersion.trim(),
        updateUrl: cfg.updateUrl?.trim() || null,
        message: cfg.message?.trim() || null,
        autoSyncEnabled: cfg.autoSyncEnabled,
        autoSyncIntervalDays: cfg.autoSyncIntervalDays,
      })
      toast.success('Kaydedildi')
      load()
    } catch (e: any) { toast.error(e?.message || 'Kaydedilemedi') }
    finally { setSaving(false) }
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>

  return (
    <div className="max-w-2xl space-y-5">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-800">
          <strong>Min Sürüm</strong>'ün altındaki tüm eklentiler <strong>devre dışı</strong> kalır (sert blok).
          Yeni sürüm yayınladıktan sonra Min Sürüm'ü o sürüme çek → eski sürümler ~15 dk içinde kapanır.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Sürüm (zorunlu)</label>
            <input value={cfg.minVersion} onChange={e => setCfg({ ...cfg, minVersion: e.target.value })}
              placeholder="örn. 5.5" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <p className="text-xs text-gray-500 mt-1">Bunun altındaki sürümler bloke olur.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Son Sürüm (zorunlu)</label>
            <input value={cfg.latestVersion} onChange={e => setCfg({ ...cfg, latestVersion: e.target.value })}
              placeholder="örn. 5.5" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <p className="text-xs text-gray-500 mt-1">Kullanıcıya gösterilen güncel sürüm.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Güncelleme Linki</label>
          <input value={cfg.updateUrl || ''} onChange={e => setCfg({ ...cfg, updateUrl: e.target.value })}
            placeholder="https://..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Devre Dışı Mesajı</label>
          <textarea value={cfg.message || ''} onChange={e => setCfg({ ...cfg, message: e.target.value })}
            placeholder="Yeni sürüm gerekli. Lütfen eklentiyi güncelleyin." rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>

        {/* Otomatik senkron (v2) — global kill switch + cadence */}
        <div className="border-t border-gray-100 pt-4 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={cfg.autoSyncEnabled}
              onChange={e => setCfg({ ...cfg, autoSyncEnabled: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300" />
            <span className="text-sm font-medium text-gray-700">Otomatik haftalık senkron (arka planda)</span>
          </label>
          <p className="text-xs text-gray-500 -mt-1 ml-7">
            Kapatılırsa hiçbir eklenti otomatik senkron yapmaz (uzaktan durdurma). Manuel "Güncelle" her zaman çalışır.
          </p>
          <div className="ml-7 flex items-center gap-2">
            <label className="text-sm text-gray-600">Aralık (gün):</label>
            <input type="number" min={1} value={cfg.autoSyncIntervalDays}
              disabled={!cfg.autoSyncEnabled}
              onChange={e => setCfg({ ...cfg, autoSyncIntervalDays: Math.max(1, Number(e.target.value) || 7) })}
              className="w-20 border border-gray-300 rounded-lg px-3 py-1.5 text-sm disabled:opacity-50" />
            <span className="text-xs text-gray-400">varsayılan 7 = haftalık</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-gray-400">
            {cfg.updatedAtUtc ? `Son güncelleme: ${new Date(cfg.updatedAtUtc).toLocaleString('tr-TR')}` : ''}
          </span>
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Kaydet
          </button>
        </div>
      </div>
    </div>
  )
}
